import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import { useI18n } from '@/contexts/I18nContext';

interface AuthPromptProps {
  isOpen: boolean;
  onClose: () => void;
  /** Contextual message explaining why auth is needed */
  message?: string;
}

/**
 * Bottom sheet shown to unauthenticated users when they attempt a gated action.
 * Stores the current URL so the user is returned here after authenticating.
 */
export const AuthPrompt: React.FC<AuthPromptProps> = ({
  isOpen,
  onClose,
  message,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();

  // Prevent body scroll while sheet is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  if (!isOpen) return null;

  const returnTo = location.pathname + location.search;

  const handleSignUp = () => {
    sessionStorage.setItem('auth_return_to', returnTo);
    navigate('/');
  };

  const handleSignIn = () => {
    sessionStorage.setItem('auth_return_to', returnTo);
    navigate('/');
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 z-40"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Bottom sheet */}
      <div
        className="fixed bottom-0 left-0 right-0 z-50 rounded-t-2xl px-6 pt-5 pb-10 bg-white"
        role="dialog"
        aria-modal="true"
      >
        {/* Handle */}
        <div className="flex justify-center mb-5">
          <div className="w-10 h-1 rounded-full bg-gray-300" />
        </div>

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-5 right-5 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Content */}
        <div className="flex flex-col gap-5">
          <div>
            <h2 className="text-gray-900 font-bold text-lg leading-snug mb-2">
              {t('auth.auth_prompt.title')}
            </h2>
            <p className="text-gray-600 text-sm leading-relaxed">
              {message ?? t('auth.auth_prompt.default_message')}
            </p>
          </div>

          <button
            onClick={handleSignUp}
            className="w-full py-3.5 rounded-full bg-sossiety-red text-white font-semibold text-sm hover:bg-sossiety-red-hover transition-colors"
          >
            {t('auth.auth_prompt.create_button')}
          </button>

          <button
            onClick={handleSignIn}
            className="w-full text-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
          >
            {t('auth.auth_prompt.sign_in_link')} —{' '}
            <span className="text-gray-900 font-medium underline underline-offset-2">{t('auth.auth_prompt.sign_in_action')}</span>
          </button>
        </div>
      </div>
    </>
  );
};
